import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Table,
  ProgressBar,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Modal,
} from "react-bootstrap";
import { collection, getDocs, query, doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import {
  BarChartFill,
  Filter,
  FileEarmarkExcel,
} from "react-bootstrap-icons";
import * as XLSX from "xlsx";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const LISTA_OCOALE = [
  "Ludus",
  "Targu Mures",
  "Reghin",
  "Gurghiu",
  "Fancel",
  "Rastolita",
  "Lunca Bradului",
  "Sovata",
  "Tarnaveni",
  "Sighisoara",
];

const obtineLunaCurentaString = () => {
  const acum = new Date();
  const an = acum.getFullYear();
  const luna = String(acum.getMonth() + 1).padStart(2, "0");
  return `${an}-${luna}`;
};

const SituatieExploatare = ({ toatePartizile = [], tema }) => {
  const { user } = useAuth();
  const [esalonari, setEsalonari] = useState([]);
  const [programeAnuale, setProgrameAnuale] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [tempProgram, setTempProgram] = useState({});
  const [filtruOcol, setFiltruOcol] = useState("Toate");
  const [filtruTip, setFiltruTip] = useState("Toate");
  const [filtruData, setFiltruData] = useState(obtineLunaCurentaString());
  const [loading, setLoading] = useState(true);
  const [contracte, setContracte] = useState([]);
  
  // Stări pentru ferestre modale și datele lor
  const [showModalEsalonari, setShowModalEsalonari] = useState(false);
  const [dateEsalonariModal, setDateEsalonariModal] = useState([]);
  
  // 1. Paginare pentru Tabelul Principal
  const [paginaCurenta, setPaginaCurenta] = useState(1);
  const randuriPePagina = 20;

  // 2. Paginare pentru Modalul de Eșalonări (Adăugat corect aici)
  const [paginaEsalonari, setPaginaEsalonari] = useState(1);
  const esalonariPePagina = 10;

  const culoriChart = {
    program: tema === "dark" ? "#81c784" : "#77b35b",
    esalonat: tema === "dark" ? "#ffd54f" : "#a18845",
    realizat: tema === "dark" ? "#4fc3f7" : "#75ccdc",
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [snapE, snapP, snapC] = await Promise.all([
          getDocs(query(collection(db, "esalonari"))),
          getDocs(query(collection(db, "program_anual"))),
          getDocs(query(collection(db, "contracte"))),
        ]);

        const dateE = snapE.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const dateC = snapC.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const prog = {};
        snapP.docs.forEach((d) => {
          prog[d.id] = d.data().volum || 0;
        });

        if (isMounted) {
          setEsalonari(dateE);
          setProgrameAnuale(prog);
          setTempProgram(prog);
          setContracte(dateC);
          setLoading(false);
        }
      } catch (err) {
        console.error("Eroare la incarcare date:", err);
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  const hartaTipuri = useMemo(() => {
    const harta = {};
    contracte.forEach((ctr) => {
      const listaPartizi =
        ctr.partiziAferente || ctr.partiziSelectateIds || ctr.partizi || [];
      const tipulGasit = ctr.tip || ctr.tipContract || ctr.tip_contract;
      if (Array.isArray(listaPartizi) && tipulGasit) {
        listaPartizi.forEach((idPartida) => {
          harta[String(idPartida).trim()] = tipulGasit;
        });
      }
    });
    return harta;
  }, [contracte]);

  // Toate datele filtrate calitativ (fără limită de paginare)
  const dateTabel = useMemo(() => {
    return toatePartizile
      .filter((p) => {
        const conditieRol = user?.rol === "administrator" || p.ocol === user?.ocol;
        const conditieOcol = filtruOcol === "Toate" || p.ocol === filtruOcol;
        const tipulPartizii = hartaTipuri[String(p.id)] || "Fără Contract";
        const conditieTip =
          filtruTip === "Toate" ||
          tipulPartizii.toLowerCase() === filtruTip.toLowerCase();
        return conditieRol && conditieOcol && conditieTip;
      })
      .map((p) => {
        const es = esalonari.filter((e) => {
          if (String(e.partidaId) !== String(p.id)) return false;
          let dataEsalonareString = "";

          if (typeof e.luna === "string" && e.luna.includes("-")) {
            dataEsalonareString = e.luna;
          } else {
            const anE = e.an || "2026";
            const lunaE = String(e.luna || "01").padStart(2, "0");
            dataEsalonareString = `${anE}-${lunaE}`;
          }
          return dataEsalonareString <= filtruData;
        });

        const totalProgramat = es.reduce(
          (acc, curr) => acc + (parseFloat(curr.volum) || 0),
          0
        );

        let totalNet = 0;
        let totalPierderi = 0;

        es.forEach((e) => {
          let netEsalonare = 0;
          let pierderiEsalonare = 0;

          if (Array.isArray(e.istoricReceptii) && e.istoricReceptii.length > 0) {
            e.istoricReceptii.forEach((r) => {
              netEsalonare +=
                parseFloat(r.volum) ||
                parseFloat(r.volumRealizat) ||
                parseFloat(r.volumNet) ||
                0;
              pierderiEsalonare +=
                parseFloat(r.volumPierderi) || parseFloat(r.pierderi) || 0;
            });
          }

          if (netEsalonare === 0 && e.volumRealizat) {
            netEsalonare = parseFloat(e.volumRealizat) || 0;
          }
          if (pierderiEsalonare === 0 && e.volumPierderi) {
            pierderiEsalonare = parseFloat(e.volumPierderi) || 0;
          }

          totalNet += netEsalonare;
          totalPierderi += pierderiEsalonare;
        });

        return {
          ...p,
          tip: hartaTipuri[String(p.id)] || "Nespecificat",
          totalProgramat,
          totalNet,
          totalPierderi,
          totalRealizat: totalNet + totalPierderi,
        };
      });
  }, [toatePartizile, filtruOcol, filtruTip, hartaTipuri, esalonari, user, filtruData]);

  // LOGICA DE PAGINARE TABEL PRINCIPAL
  const indiceUltimulRand = paginaCurenta * randuriPePagina;
  const indicePrimulRand = indiceUltimulRand - randuriPePagina;
  const randuriPaginaCurenta = dateTabel.slice(indicePrimulRand, indiceUltimulRand);
  const totalPagini = Math.ceil(dateTabel.length / randuriPePagina);

 const deschideModalEsalonari = () => {
   const dateFiltrate = esalonari
     .filter((e) => {
       const partida = toatePartizile.find(
         (p) => String(p.id) === String(e.partidaId),
       );
       if (!partida) return false;
       const conditieRol =
         user?.rol === "administrator" || partida.ocol === user?.ocol;
       const conditieOcol =
         filtruOcol === "Toate" || partida.ocol === filtruOcol;
       return conditieRol && conditieOcol;
     })
     .map((e) => {
       const partida = toatePartizile.find(
         (p) => String(p.id) === String(e.partidaId),
       );

       // --- LOGICĂ DE SIGURANȚĂ PENTRU EXTRAGERE LUNĂ ȘI AN ---
       let anAfisat = e.an || "2026";
       let lunaAfisata = e.luna || "01";

       // Dacă e.luna conține deja cratima (ex: "2026-11"), o spargem în componente clare
       if (typeof e.luna === "string" && e.luna.includes("-")) {
         const parti = e.luna.split("-");
         anAfisat = parti[0];
         lunaAfisata = parti[1];
       } else {
         lunaAfisata = String(lunaAfisata).padStart(2, "0");
       }

       return {
         ...e,
         numeOcol: partida?.ocol || "Nespecificat",
         numarPartida: partida?.numarPartida || partida?.fond || "-",
         volNum: parseFloat(e.volum) || 0,
         realizNum: parseFloat(e.volumRealizat) || 0,
         anCurat: anAfisat,
         lunaCurata: lunaAfisata,
       };
     })
     .sort((a, b) => {
       if (a.anCurat !== b.anCurat)
         return Number(a.anCurat) - Number(b.anCurat);
       return Number(a.lunaCurata) - Number(b.lunaCurata);
     });

   setPaginaEsalonari(1);
   setDateEsalonariModal(dateFiltrate);
   setShowModalEsalonari(true);
 };

 const handleInchideModalEsalonari = () => {
   setShowModalEsalonari(false);
   setPaginaEsalonari(1);
 };

 const totaluriModal = dateEsalonariModal.reduce(
   (acc, curr) => {
     acc.volum += curr.volNum;
     acc.realizat += curr.realizNum;
     return acc;
   },
   { volum: 0, realizat: 0 },
 );

 const indiceUltimulRandModal = paginaEsalonari * esalonariPePagina;
 const indicePrimulRandModal = indiceUltimulRandModal - esalonariPePagina;

 const esalonariPaginaCurenta = dateEsalonariModal.slice(
   indicePrimulRandModal,
   indiceUltimulRandModal,
 );
 const totalPaginiEsalonari = Math.ceil(
   dateEsalonariModal.length / esalonariPePagina,
 );

 const exportaEsalonariExcel = () => {
   const dateExport = dateEsalonariModal.map((e) => ({
     "Ocol Silvic": e.numeOcol,
     Partida: e.numarPartida,
     "Luna/An": `${e.lunaCurata}-${e.anCurat}`, // Securizat ca să nu mai exporte undefined
     "Volum Esalonat (mc)": e.volNum,
     "Volum Realizat (mc)": e.realizNum,
     "Rest de realizat": e.volNum - e.realizNum,
   }));

   const ws = XLSX.utils.json_to_sheet(dateExport);
   const wb = XLSX.utils.book_new();
   XLSX.utils.book_append_sheet(wb, ws, "Esalonari");
   XLSX.writeFile(
     wb,
     `Esalonari_${filtruOcol}_${new Date().toLocaleDateString()}.xlsx`,
   );
 };

  const dateGrafic = useMemo(() => {
    const dateGrupate = {};

    Object.entries(programeAnuale).forEach(([ocolNume, volumProg]) => {
      const esteAdmin = user?.rol === "administrator";
      const esteOcolulMeu =
        user?.ocol && ocolNume.trim().toLowerCase() === user.ocol.trim().toLowerCase();

      if (esteAdmin || esteOcolulMeu) {
        dateGrupate[ocolNume] = {
          name: ocolNume,
          programAnual: Number(volumProg || 0),
          esalonat: 0,
          realizat: 0,
        };
      }
    });

    dateTabel.forEach((p) => {
      const ocol = p.ocol;
      if (!dateGrupate[ocol]) {
        const esteAdmin = user?.rol === "administrator";
        const esteOcolulMeu =
          user?.ocol && ocol.trim().toLowerCase() === user.ocol.trim().toLowerCase();

        if (esteAdmin || esteOcolulMeu) {
          dateGrupate[ocol] = {
            name: ocol,
            programAnual: 0,
            esalonat: 0,
            realizat: 0,
          };
        }
      }

      if (dateGrupate[ocol]) {
        dateGrupate[ocol].esalonat += Number(p.totalProgramat || 0);
        dateGrupate[ocol].realizat += Number(p.totalRealizat || 0);
      }
    });

    let final = Object.values(dateGrupate);
    if (filtruOcol !== "Toate") {
      final = final.filter((item) => item.name === filtruOcol);
    }
    return final;
  }, [dateTabel, programeAnuale, filtruOcol, user]);

  const calculeazaStats = useCallback(
    (ocolNume) => {
      if (!toatePartizile || toatePartizile.length === 0) {
        return {
          prog: parseFloat(programeAnuale[ocolNume]) || 0,
          esalTotal: 0,
          esalPanaAzi: 0,
          real: 0,
          pierderi: 0,
        };
      }

      const ocolCurat = ocolNume.trim().toLowerCase();
      const partiziOcol = toatePartizile.filter(
        (p) => p.ocol?.toString().trim().toLowerCase() === ocolCurat
      );

      let esalTotal = 0;
      let esalPanaAzi = 0;
      let realTotal = 0;
      let pierderiTotal = 0;

      partiziOcol.forEach((p) => {
        const es = esalonari.filter((e) => String(e.partidaId) === String(p.id));

        es.forEach((e) => {
          const volEsalonat = parseFloat(e.volum) || 0;
          esalTotal += volEsalonat;

          let dataEsalonareString = "";
          if (typeof e.luna === "string" && e.luna.includes("-")) {
            dataEsalonareString = e.luna;
          } else {
            const anE = e.an || "2026";
            const lunaE = String(e.luna || "01").padStart(2, "0");
            dataEsalonareString = `${anE}-${lunaE}`;
          }

          if (dataEsalonareString <= filtruData) {
            esalPanaAzi += volEsalonat;
            let netE = 0;
            let pierdE = 0;

            if (Array.isArray(e.istoricReceptii) && e.istoricReceptii.length > 0) {
              e.istoricReceptii.forEach((r) => {
                netE +=
                  parseFloat(r.volum) ||
                  parseFloat(r.volumRealizat) ||
                  parseFloat(r.volumNet) ||
                  0;
                pierdE += parseFloat(r.volumPierderi) || parseFloat(r.pierderi) || 0;
              });
            }

            if (netE === 0 && e.volumRealizat) {
              netE = parseFloat(e.volumRealizat) || 0;
            }
            if (pierdE === 0 && e.volumPierderi) {
              pierdE = parseFloat(e.volumPierderi) || 0;
            }

            realTotal += netE + pierdE;
            pierderiTotal += pierdE;
          }
        });
      });

      return {
        prog: parseFloat(programeAnuale[ocolNume]) || 0,
        esalTotal,
        esalPanaAzi,
        real: realTotal,
        pierderi: pierderiTotal,
      };
    },
    [toatePartizile, esalonari, programeAnuale, filtruData]
  );

  const dateJudet = useMemo(() => {
    return LISTA_OCOALE.reduce(
      (acc, ocol) => {
        const s = calculeazaStats(ocol);
        acc.prog += s.prog;
        acc.esal += s.esalPanaAzi;
        acc.real += s.real;
        acc.pierderi += s.pierderi;
        return acc;
      },
      { prog: 0, esal: 0, real: 0, pierderi: 0 }
    );
  }, [calculeazaStats]);

  const exportaExcel = () => {
    const dateExport = dateTabel.map((item) => ({
      "Ocol Silvic": item.ocol,
      Partida: item.numarPartida ? `${item.fond} (${item.numarPartida})` : item.fond,
      SUMAL: item.sumal,
      Tip: item.tip,
      "Natura Produs": item.natura,
      "Volum Eșalonat (mc)": item.totalProgramat,
      "Volum Realizat (mc)": item.totalRealizat,
    }));
    const ws = XLSX.utils.json_to_sheet(dateExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Situatie Exploatare");
    XLSX.writeFile(wb, `Situatie_Exploatare_${new Date().toLocaleDateString()}.xlsx`);
  };

  const salveazaProgram = async () => {
    for (const ocol of LISTA_OCOALE) {
      await setDoc(doc(db, "program_anual", ocol), {
        volum: parseFloat(tempProgram[ocol]) || 0,
      });
    }
    setProgrameAnuale({ ...tempProgram });
    setEditMode(false);
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="success" />
        <p className="mt-2">Se sincronizează datele...</p>
      </Container>
    );
  }

  const totalTabel = dateTabel.reduce(
    (acc, curr) => {
      acc.brut += parseFloat(curr.volBrut) || 0;
      acc.esal += curr.totalProgramat || 0;
      acc.net += curr.totalNet || 0;
      acc.pierderi += curr.totalPierderi || 0;
      acc.realTotal += curr.totalRealizat || 0;
      return acc;
    },
    { brut: 0, esal: 0, net: 0, pierderi: 0, realTotal: 0 }
  );

  return (
    <Container fluid className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3
          className="fw-bold"
          style={{ color: tema === "dark" ? "#0d6efd" : "#318155" }}
        >
          <BarChartFill className="me-2" />
          Situația Exploatării
        </h3>
        {user?.rol === "administrator" && (
          <Button
            variant={editMode ? "success" : "outline-primary"}
            onClick={() => (editMode ? salveazaProgram() : setEditMode(true))}
          >
            {editMode ? "Salvează Programul" : "Editează Program Anual"}
          </Button>
        )}
      </div>

      {/* CARD TOTAL JUDEȚ */}
      <Card
        className="mb-4 border-0 shadow-sm"
        style={{
          color: tema === "dark" ? "#0d6efd" : "#fdfdfd",
          background:
            tema === "dark"
              ? "linear-gradient(45deg, #232731 14%, #393852 100%)"
              : "linear-gradient(45deg, #7eb592 14%, #465d42 100%)",
          borderRadius: "12px",
        }}
      >
        <Card.Body>
          <Row className="align-items-center">
            <Col md={4}>
              <h5>TOTAL REALIZAT / PROGRAM D.S.</h5>
              <ProgressBar
                className="progress-bar-striped progress-bar-animated opacity-75"
                now={
                  dateJudet.prog > 0
                    ? (dateJudet.real / dateJudet.prog) * 100
                    : 0
                }
                variant="success"
                style={{
                  height: "8px",
                  backgroundColor:
                    tema === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(165, 186, 158, 0.78)",
                }}
              />
            </Col>
            <Col md={2} className="text-center">
              <small>Program</small>
              <div>{dateJudet.prog.toLocaleString()} mc</div>
            </Col>
            <Col md={2} className="text-center">
              <small>Eșalonat</small>
              <div>{dateJudet.esal.toLocaleString()} mc</div>
            </Col>
            <Col md={2} className="text-center">
              <small>Realizat</small>
              <div className="fw-bold">
                {dateJudet.real.toLocaleString()} mc
              </div>
            </Col>
            <Col md={2} className="text-center">
              <small>Grad</small>
              <div>
                {(dateJudet.prog > 0
                  ? (dateJudet.real / dateJudet.prog) * 100
                  : 0
                ).toFixed(1)}{" "}
                %
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* CARDURI OCOALE */}
      <Row className="mb-4 row-cols-md-5 g-3 text-center">
        {LISTA_OCOALE.map((numeOcol) => {
          const esteAdmin = user?.rol === "administrator";
          const esteOcolulMeu =
            user?.ocol &&
            numeOcol.trim().toLowerCase() === user.ocol.trim().toLowerCase();

          if (!esteAdmin && !esteOcolulMeu) return null;

          const s = calculeazaStats(numeOcol);
          const procentAnual = s.prog > 0 ? (s.real / s.prog) * 100 : 0;
          const isDark = tema === "dark";

          return (
            <Col key={numeOcol}>
              <Card
                className="h-100 shadow-sm border-0"
                style={{
                  backgroundColor: isDark ? "#212529" : "#ffffff",
                  color: isDark ? "#e9ecef" : "#212529",
                  borderTop: `4px solid ${isDark ? "#0d6efd" : "#198754"}`,
                  borderRadius: "10px",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateY(-5px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateY(0)")
                }
              >
                <Card.Body className="p-3">
                  <h6
                    className="fw-bold mb-3 pb-2"
                    style={{
                      fontSize: "0.95rem",
                      borderBottom: `1px solid ${isDark ? "#373b3e" : "#f1f3f5"}`,
                      color: isDark ? "#60a5fa" : "#198754",
                    }}
                  >
                    {numeOcol}
                  </h6>

                  {editMode ? (
                    <Form.Control
                      size="sm"
                      type="number"
                      className={
                        isDark ? "bg-dark text-white border-secondary" : ""
                      }
                      value={tempProgram[numeOcol] || ""}
                      onChange={(e) =>
                        setTempProgram({
                          ...tempProgram,
                          [numeOcol]: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <div style={{ fontSize: "0.85rem" }}>
                      <div className="d-flex justify-content-between mb-2">
                        <span style={{ opacity: 0.8 }}>Program:</span>
                        <span
                          className="fw-bold"
                          style={{ color: isDark ? "#60a5fa" : "#198754" }}
                        >
                          {s.prog.toLocaleString()}
                        </span>
                      </div>

                      <div
                        className="d-flex justify-content-between p-2 mb-2 rounded"
                        style={{
                          backgroundColor: isDark
                            ? "rgba(13, 110, 253, 0.1)"
                            : "#f8f9fa",
                        }}
                      >
                        <span style={{ opacity: 0.8 }}>Eșalonat la zi:</span>
                        <span
                          className="fw-bold"
                          style={{ color: isDark ? "#60a5fa" : "#1d9c4f" }}
                        >
                          {s.esalPanaAzi.toLocaleString()}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between p-2 mb-2">
                        <span style={{ opacity: 0.8 }}>Realizat:</span>
                        <span
                          className={`fw-bold ${s.real < s.esalPanaAzi ? "text-danger" : isDark ? "text-info" : "text-success"}`}
                        >
                          {s.real.toLocaleString()}
                        </span>
                      </div>

                      <div
                        className="d-flex justify-content-between p-2 rounded mb-3"
                        style={{
                          color: isDark ? "#f78e8e" : "#ef162c",
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.03)"
                            : "rgba(0,0,0,0.02)",
                        }}
                      >
                        <span style={{ fontSize: "0.75rem" }}>
                          Din care pierderi:
                        </span>
                        <span
                          className="fw-bold"
                          style={{
                            fontSize: "0.75rem",
                            color: isDark ? "#f78e8e" : "#ef162c",
                          }}
                        >
                          {s.pierderi.toLocaleString()}
                        </span>
                      </div>

                      <div className="mt-2">
                        <div
                          className="d-flex justify-content-between mb-1"
                          style={{ fontSize: "0.7rem" }}
                        >
                          <span>Progres Anual</span>
                          <span>{procentAnual.toFixed(1)}%</span>
                        </div>
                        <ProgressBar
                          className="progress-bar-striped progress-bar-animated"
                          now={procentAnual}
                          style={{
                            height: "6px",
                            backgroundColor: isDark ? "#3e454b" : "#c6d6c4ea",
                          }}
                          children={
                            <div
                              className="progress-bar"
                              style={{
                                width: `${procentAnual}%`,
                                backgroundColor:
                                  s.real < s.esalPanaAzi
                                    ? "#f25d5f"
                                    : isDark
                                      ? "#3b82f6"
                                      : "#198754",
                                borderRadius: "10px",
                              }}
                            />
                          }
                        />
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* FILTRE TABEL */}
      <Card className="mb-3 shadow-sm border-0">
        <Card.Body className="py-2 d-flex flex-wrap gap-3 align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Filter /> <strong>Filtre:</strong>
          </div>
          {user?.rol === "administrator" && (
            <Form.Select
              size="sm"
              style={{ width: "200px" }}
              value={filtruOcol}
              onChange={(e) => {
                setFiltruOcol(e.target.value);
                setPaginaCurenta(1);
              }}
            >
              <option value="Toate">Toate Ocoalele</option>
              {LISTA_OCOALE.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Form.Select>
          )}
          <Form.Select
            size="sm"
            style={{ width: "200px" }}
            value={filtruTip}
            onChange={(e) => {
              setFiltruTip(e.target.value);
              setPaginaCurenta(1);
            }}
          >
            <option value="Toate">Toate Tipurile</option>
            <option value="prestari">Prestări</option>
            <option value="regie">Regie</option>
          </Form.Select>

          <div className="d-flex align-items-center gap-2 border-start ps-3">
            <Form.Control
              size="sm"
              type="month"
              style={{ width: "170px",
                apearance: "auto",
                WebkitAppearance: "auto",
               }}
              value={filtruData}
              max={obtineLunaCurentaString()}
              onChange={(e) => {
                if (e.target.value) {
                  setFiltruData(e.target.value);
                  setPaginaCurenta(1);
                }
              }}
            />
          </div>

          <div className="ms-auto d-flex gap-2">
            <Button variant="outline-success" size="sm" onClick={exportaExcel}>
              <FileEarmarkExcel size={20} className="me-1" /> Exportă în Excel
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={deschideModalEsalonari}
            >
              <BarChartFill className="me-1" /> Detalii Eșalonări
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* GRAFIC ANALIZĂ */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <h5
            className="mb-4"
            style={{ color: tema === "dark" ? "#5297ff" : "#3a563b" }}
          >
            Analiză Progres: {filtruOcol}
          </h5>
          <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer>
              <BarChart data={dateGrafic}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  formatter={(value) =>
                    new Intl.NumberFormat("ro-RO").format(value) + " mc"
                  }
                />
                <Legend />
                <Bar
                  dataKey="programAnual"
                  name="Program Anual"
                  fill={culoriChart.program}
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                />
                <Bar
                  dataKey="esalonat"
                  name="Total Eșalonat"
                  fill={culoriChart.esalonat}
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                />
                <Bar
                  dataKey="realizat"
                  name="Total Realizat"
                  fill={culoriChart.realizat}
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card.Body>
      </Card>

      {/* TABEL DETALIAT PRINCIPAL */}
      <Table
        responsive
        hover
        striped
        className="shadow-sm rounded overflow-hidden mb-0"
      >
        <thead className="table-success text-uppercase small">
          <tr>
            <th style={{ width: "25%" }}>Partidă / Ocol</th>
            <th style={{ width: "10%" }}>Vol. Brut</th>
            <th style={{ width: "12%" }}>Eșalonat</th>
            <th style={{ width: "12%" }}>Realizat (Net)</th>
            <th style={{ width: "10%" }}>Pierderi</th>
            <th style={{ width: "12%" }}>Total (N+P)</th>
            <th style={{ width: "19%" }} className="text-center">
              Progres/Status
            </th>
          </tr>
        </thead>
        <tbody>
          {randuriPaginaCurenta.map((p) => {
            const volBrut = Number(p.volBrut) || 0;
            const procent = volBrut > 0 ? (p.totalRealizat / volBrut) * 100 : 0;

            return (
              <tr key={p.id}>
                <td>
                  <div className="fw-bold">{p.fond}</div>
                  <small>{p.ocol}</small>
                </td>
                <td>{volBrut}</td>
                <td className="text-primary fw-bold">
                  {p.totalProgramat.toFixed(2)}
                </td>
                <td>{p.totalNet.toFixed(2)}</td>
                <td className="text-danger small">
                  {p.totalPierderi.toFixed(2)}
                </td>
                <td className="fw-bold">{p.totalRealizat.toFixed(2)}</td>
                <td>
                  <div className="d-flex justify-content-between">
                    <small>{procent.toFixed(1)}%</small>
                  </div>
                  <ProgressBar
                    now={procent}
                    variant={procent > 100 ? "danger" : "success"}
                    style={{ height: "5px" }}
                  />
                </td>
              </tr>
            );
          })}
          {randuriPaginaCurenta.length === 0 && (
            <tr>
              <td colSpan="7" className="text-center py-4 opacity-75">
                Nu există partizi care să corespundă filtrelor selectate.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot className="fw-bold">
          <tr>
            <td>TOTAL</td>
            <td>{totalTabel.brut.toFixed(0)}</td>
            <td className="text-primary">{totalTabel.esal.toFixed(2)}</td>
            <td>{totalTabel.net.toFixed(2)}</td>
            <td className="text-danger">{totalTabel.pierderi.toFixed(2)}</td>
            <td className="text-success">{totalTabel.realTotal.toFixed(2)}</td>
            <td>
              <div className="d-flex flex-column">
                <small style={{ fontSize: "0.7rem" }}>Eficiență:</small>
                <span>
                  {(totalTabel.brut > 0
                    ? (totalTabel.realTotal / totalTabel.brut) * 100
                    : 0
                  ).toFixed(1)}{" "}
                  %
                </span>
              </div>
            </td>
          </tr>
        </tfoot>
      </Table>

      {/* CONTROALE PAGINARE TABEL PRINCIPAL */}
      {totalPagini > 1 && (
        <div className="d-flex justify-content-between align-items-center my-3 px-2">
          <div className="small text-muted">
            Se afișează partizile <strong>{indicePrimulRand + 1}</strong> -{" "}
            <strong>{Math.min(indiceUltimulRand, dateTabel.length)}</strong> din
            totalul de <strong>{dateTabel.length}</strong> înregistrări
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={paginaCurenta === 1}
              onClick={() => setPaginaCurenta((prev) => Math.max(prev - 1, 1))}
            >
              Înapoi
            </Button>
            <div className="d-flex align-items-center px-2 small fw-bold">
              Pagina {paginaCurenta} din {totalPagini}
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={paginaCurenta === totalPagini}
              onClick={() =>
                setPaginaCurenta((prev) => Math.min(prev + 1, totalPagini))
              }
            >
              Înainte
            </Button>
          </div>
        </div>
      )}

      {/* MODAL DETALII EȘALONĂRI */}
      <Modal
        show={showModalEsalonari}
        onHide={handleInchideModalEsalonari}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalii Eșalonări active</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table responsive striped bordered hover size="sm">
            <thead className="table-dark small">
              <tr>
                <th>Ocol</th>
                <th>Partidă</th>
                <th>Dată</th>
                <th>Volum programat (mc)</th>
                <th>Volum realizat (mc)</th>
              </tr>
            </thead>
            <tbody>
              {esalonariPaginaCurenta.map((e) => (
                <tr key={e.id}>
                  <td>{e.numeOcol}</td>
                  <td>{e.numarPartida}</td>
                  <td>{`${e.lunaCurata}-${e.anCurat}`}</td>
                  <td>{e.volNum.toLocaleString("ro-RO")}</td>
                  <td>{e.realizNum.toLocaleString("ro-RO")}</td>
                </tr>
              ))}
              {esalonariPaginaCurenta.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-3">
                    Nu sunt date disponibile în modal.
                  </td>
                </tr>
              )}
            </tbody>
            {dateEsalonariModal.length > 0 && (
              <tfoot style={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                <tr>
                  <td colSpan="3" className="text-end">
                    Total General Filtrat:
                  </td>
                  <td className="text-primary">
                    {totaluriModal.volum.toLocaleString("ro-RO")}
                  </td>
                  <td className="text-success">
                    {totaluriModal.realizat.toLocaleString("ro-RO")}
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>

          {/* Controale paginare specifice pentru interiorul Modalului */}
          {totalPaginiEsalonari > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3 px-1">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  setPaginaEsalonari((prev) => Math.max(prev - 1, 1))
                }
                disabled={paginaEsalonari === 1}
              >
                &laquo; Înapoi
              </Button>

              <span className="text-muted small">
                Pagina <strong>{paginaEsalonari}</strong> din{" "}
                <strong>{totalPaginiEsalonari}</strong> (
                {dateEsalonariModal.length} înregistrări)
              </span>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  setPaginaEsalonari((prev) =>
                    Math.min(prev + 1, totalPaginiEsalonari),
                  )
                }
                disabled={paginaEsalonari === totalPaginiEsalonari}
              >
                Înainte &raquo;
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={exportaEsalonariExcel}>
            Exportă Eșalonări
          </Button>
          <Button variant="secondary" onClick={handleInchideModalEsalonari}>
            Închide
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SituatieExploatare;